import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AU_PHONE_PATTERN_NO_COUNTRY_CODE, COUNTRY_CODES, defaultPattern } from 'avn/services/country-codes';
import { ValidatorComponent } from 'avn/shared/validator/validator.component';

@Component({
  selector: 'app-tel-input',
  templateUrl: './tel-input.component.html',
  styleUrls: ['./tel-input.component.scss']
})
export class TelInputComponent extends ValidatorComponent implements OnInit, OnDestroy, DoCheck {
  constructor(
    private fb: FormBuilder,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    super();
  }

  // ================================== Template ================================== //
  // <app-tel-input label="Contact number" [readOnly]="true" [required]="true" [showLabel]="true" [showErrorLabel]="true" [value]="phoneObject" (phoneGroupEmit)="phoneGroupEmit($event)"></app-tel-input>
  // ================================== Template ================================== //

  @ViewChild('countryCodeSearchInput') countryCodeSearchInput: ElementRef;
  @ViewChild('phoneInput') phoneInput: ElementRef<HTMLInputElement>;

  private activePhoneCodeGroup: HTMLElement | null = null;

  @Input() set value(data: { phone_number: string; country_code: string }) {
    this.inputValue = data || null;
    this.syncInputValue(this.inputValue);
  }

  @Input() label: string;
  @Input() readOnly = false;
  @Input() required = true;
  @Input() showLabel = true;
  @Input() showErrorLabel = false;
  @Input() parentClass = 'field-type-1';

  @Output() phoneGroupEmit = new EventEmitter<FormGroup>();

  phoneGroup = this.fb.group({
    phone_number: [''],
    country_code: ['+61']
  });

  countryCodes = COUNTRY_CODES;
  phoneNumberPattern = defaultPattern;

  showDropdown = false;

  searchedCode = '';

  eventListener: any;
  private inputValue: { phone_number: string; country_code: string } | null = null;
  private lastAppliedValue = { phone_number: '', country_code: '+61' };
  private failedFlagCodes = new Set<string>();

  ngOnInit(): void {
    this.phoneGroup.setValidators(this.validatePhoneNumber());
    this.phoneGroup.updateValueAndValidity({ emitEvent: false });
    this.phoneGroupEmit.emit(this.phoneGroup);

    this.phoneGroup.valueChanges.subscribe(() => {
      this.phoneGroupEmit.emit(this.phoneGroup);
    });
  }

  ngDoCheck(): void {
    if (!this.inputValue) return;
    const normalized = this.normalizeValue(this.inputValue);
    if (
      normalized.phone_number !== this.lastAppliedValue.phone_number ||
      normalized.country_code !== this.lastAppliedValue.country_code
    ) {
      this.syncInputValue(this.inputValue);
    }
  }

  ngOnDestroy(): void {
    this.removeListener();
  }

  focusPhoneInput() {
    if (this.readOnly) return;
    this.phoneInput?.nativeElement?.focus();
  }

  dropdownClick(parent: any) {
    this.searchedCode = '';
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) {
      this.countryCodeSearchInput.nativeElement.value = '';
      this.activePhoneCodeGroup = parent;
      parent?.classList?.add('input-focused');
      if (isPlatformBrowser(this.platformId)) {
        document.addEventListener('click', this.createListener, false);
        setTimeout(() => {
          this.countryCodeSearchInput?.nativeElement?.focus();
        }, 200);
      }
    } else {
      parent?.classList?.remove('input-focused');
      this.activePhoneCodeGroup = null;
      this.removeListener();
    }
  }

  createListener = (event: any) => {
    const dropdown = this.countryCodeSearchInput?.nativeElement?.closest('.tel-input-box') as HTMLElement | null;
    if (dropdown && !dropdown.contains(event.target)) {
      this.activePhoneCodeGroup?.classList?.remove('input-focused');
      this.activePhoneCodeGroup = null;
      this.showDropdown = false;
      this.removeListener();
    }
  };

  removeListener() {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.createListener, false);
    }
  }

  selectCountry(country: any) {
    this.searchedCode = '';
    this.countryCodeSearchInput.nativeElement.value = '';
    this.phoneGroup.get('country_code')?.setValue(country.code);
    this.phoneNumberPattern = country.pattern;
    this.showDropdown = false;
    this.activePhoneCodeGroup?.classList?.remove('input-focused');
    this.activePhoneCodeGroup = null;
    this.removeListener();
    this.phoneGroup.updateValueAndValidity();
  }

  validatePhoneNumber(): ValidatorFn {
    return (): ValidationErrors | null => {
      const ctry_code = this.country_code?.value;
      const phone_no = this.phone_number?.value;
      if (this.validatePhoneNumberwithPattern(phone_no, ctry_code)) {
        const phone: string = (phone_no || '')?.replace(/ /g, '');
        if (
          ctry_code === '+61' &&
          phone.length === 8 &&
          phone[0] !== '4' &&
          !this.validatePhoneNumberwithPattern('02' + phone, ctry_code)
        ) {
          // this.shared.showSnackBar('Invalid phone number, check if area code is missing', 5);
          return { areacode: true };
        }
        // this.shared.showSnackBar('Please enter valid phone number.', 5);
        return { invalid: true };
      }
      return null;
    };
  }

  validatePhoneNumberwithPattern(phone_no: any, country_code: any): boolean {
    const safePhone = phone_no || '';
    if (country_code === '+61') {
      const localValid = AU_PHONE_PATTERN_NO_COUNTRY_CODE.test(safePhone);
      const combinedPattern = this.phoneNumberPattern || defaultPattern;
      const combinedValid = combinedPattern.test(`${country_code}${safePhone}`);
      return !(localValid || combinedValid);
    }
    const combinedNumber = `${country_code || ''}${safePhone}`;
    if (this.phoneNumberPattern) {
      return !this.phoneNumberPattern.test(combinedNumber);
    }
    return true;
  }

  searchCountryCodes(e: InputEvent) {
    this.searchedCode = (e.target as HTMLInputElement).value || '';
  }

  getCountryCodes() {
    if (this.searchedCode) {
      return this.countryCodes.filter(
        (x) => x.code.toLowerCase().includes(this.searchedCode) || x.country.toLowerCase().includes(this.searchedCode)
      );
    }
    return this.countryCodes;
  }

  get phone_number() {
    return this.phoneGroup.get('phone_number');
  }

  get country_code() {
    return this.phoneGroup.get('country_code');
  }

  getSelectedCountry() {
    return this.countryCodes.find((x: any) => x.code === this.country_code?.value);
  }

  getCountryFlagImageUrl(shortCode: string | undefined) {
    const normalizedCode = this.normalizeShortCode(shortCode);
    if (!normalizedCode) return '';
    return `https://flagcdn.com/24x18/${normalizedCode.toLowerCase()}.png`;
  }

  shouldUseFlagImage(shortCode: string | undefined) {
    const normalizedCode = this.normalizeShortCode(shortCode);
    if (!normalizedCode) return false;
    return !this.failedFlagCodes.has(normalizedCode);
  }

  onFlagImageError(event: Event, shortCode: string | undefined) {
    const normalizedCode = this.normalizeShortCode(shortCode);
    if (normalizedCode) {
      this.failedFlagCodes.add(normalizedCode);
    }
    const imageElement = event.target as HTMLImageElement | null;
    if (imageElement) {
      imageElement.style.display = 'none';
    }
  }

  private normalizeValue(data: { phone_number: string; country_code: string } | null) {
    return {
      phone_number: data?.phone_number || '',
      country_code: data?.country_code || '+61'
    };
  }

  private syncInputValue(data: { phone_number: string; country_code: string } | null) {
    if (!data) return;
    const normalized = this.normalizeValue(data);

    this.phone_number?.setValue(normalized.phone_number || '', { emitEvent: false });
    this.country_code?.setValue(normalized.country_code || '+61', { emitEvent: false });
    this.phoneNumberPattern =
      this.countryCodes.find((x: any) => x.code === this.country_code?.value)?.pattern || defaultPattern;

    if (normalized.phone_number) {
      this.phoneGroup.markAsDirty();
    }

    this.phoneGroup.updateValueAndValidity({ emitEvent: false });
    this.phoneGroupEmit.emit(this.phoneGroup);
    this.lastAppliedValue = normalized;
  }

  private normalizeShortCode(shortCode: string | undefined) {
    return (shortCode || '').toString().trim().toUpperCase().slice(0, 2);
  }
}
